import React, { useEffect, useMemo, useState } from 'react';
import { Formik } from 'formik';
import { Modal } from 'reactstrap';
import { Common } from '../../core/common';
import Translator from '../../core/i18n';
import { ActivityType } from '../../models/enums/ActivityType';
import { QuestionType } from '../../models/enums/QuestionType';
import { SelectNoneAsync } from '../../components/input/select/main';
import ExcelTemplateDownload from '../../components/file-download/excel-template/main';
import { FileUpload } from '../../components/file-upload/main';
import { Guid } from 'guid-typescript';
import { SetFileMultiUploadId, SetRefreshUploadMultiQuestion } from '../../store/actions/question';
import { IRefreshUploadMultiQuestionViewModel } from '../../models/action/question';
import { useDispatch } from 'react-redux';
import { Log } from 'ng2-logger';
import { GetSaveUploadedQuestions, UploadMultiQuestionsExcel } from '../../store/actions/background';
import { Notification } from '../../components/notification/main';

interface AddMultipleQuestionProps {
    isModalOpen: boolean;
    setCloseModal: () => void;
    uploadMultiQuestionResult: IRefreshUploadMultiQuestionViewModel
}

const _logger = Log.create('Add Multi Question');
const _common = new Common();
const T = Translator.create();

const AddMultipleQuestion: React.FC<AddMultipleQuestionProps> = ({ isModalOpen, setCloseModal, uploadMultiQuestionResult }) => {

    const dispatch = useDispatch();

    const fileInputKey = useMemo(() => Guid.create().toString(), []);
    const [validquestionCount, setValidquestionCount] = useState(0);
    const [allquestionCount, setAllquestionCount] = useState(0);
    const [invalidquestionCount, setInvalidquestionCount] = useState(0);
    const [wrongQuestionsFilePath, setWrongQuestionsFilePath] = useState('');
    const [fileUploadName, setFileUploadName] = useState('');
    const [isExcelUploaded, setIsExcelUploaded] = useState(false);
    const [isCompleted, setIsCompleted] = useState(false)
    const [isLoadingProcess, setIsLoadingProcess] = useState(false)
    const [questionTypeDistributionArray, setQuestionTypeDistributionArray] = useState<Array<{ returnTypeName: string, validCount: number, invalidCount: number }>>([]);

    useEffect(() => {
        if (uploadMultiQuestionResult) {
            setValidquestionCount(uploadMultiQuestionResult.validQuestionsCount);
            setAllquestionCount(uploadMultiQuestionResult.totalQuestionsCount);
            setInvalidquestionCount(uploadMultiQuestionResult.invalidQuestionsCount);
            setWrongQuestionsFilePath(uploadMultiQuestionResult.wrongQuestionsFilePath)

            if (uploadMultiQuestionResult.totalQuestionsCount > 0) {
                setIsExcelUploaded(true);
                setIsLoadingProcess(false)
            } else {
                setIsExcelUploaded(false)
                setIsLoadingProcess(false)
            }
        } else {
            setIsExcelUploaded(false)
            setIsLoadingProcess(false)
        }
    }, [uploadMultiQuestionResult])

    useEffect(() => {
        if (fileInputKey && fileInputKey.length > 0) {
            dispatch(SetFileMultiUploadId(fileInputKey, "setFileMultiUploadId", "set"));
        }
    }, [fileInputKey]);

    useEffect(() => {
        if (isCompleted) {
            refreshFileUpload()
            setCloseModal();
        }
    }, [isCompleted]);

    useEffect(() => {

        if (uploadMultiQuestionResult.allQuestionTypeDistribution && uploadMultiQuestionResult.validQuestionTypeDistribution && uploadMultiQuestionResult.invalidQuestionTypeDistribution) {
            const updatedDistributionArray = Object.keys(uploadMultiQuestionResult.allQuestionTypeDistribution).map((type) => {

                const returnTypeName = type;
                const typeName = type as keyof typeof QuestionType;
                const validCount = uploadMultiQuestionResult.validQuestionTypeDistribution![typeName] ?? 0;
                const invalidCount = uploadMultiQuestionResult.invalidQuestionTypeDistribution![typeName] ?? 0;

                return { returnTypeName, validCount, invalidCount };
            });
            setQuestionTypeDistributionArray(updatedDistributionArray);
        } else {
            setQuestionTypeDistributionArray([]);
        }

    }, [uploadMultiQuestionResult.allQuestionTypeDistribution, uploadMultiQuestionResult.validQuestionTypeDistribution, uploadMultiQuestionResult.invalidQuestionTypeDistribution]);

    const onSubmit = (values: any) => {
        console.log("formaik values", values);
    };

    const refreshFileUpload = () => {
        const defaultRefreshUploadMultiQuestionModel: IRefreshUploadMultiQuestionViewModel = {
            totalQuestionsCount: 0,
            validQuestionsCount: 0,
            invalidQuestionsCount: 0,
            allQuestionTypeDistribution: undefined,
            validQuestionTypeDistribution: undefined,
            invalidQuestionTypeDistribution: undefined,
            wrongQuestionsFilePath: ''

        };
        dispatch(SetRefreshUploadMultiQuestion(defaultRefreshUploadMultiQuestionModel, "setRefreshUploadMultiQuestion", "set"))
        setIsCompleted(false);
    }

    const saveQuestions = (isSave: boolean) => {
        if (isSave && validquestionCount == 0) {
            Notification.setNotification('error', "Geçerli soru bulunmamaktadır. Lütfen tekrar kontrol ediniz.", 'topRight');
            refreshFileUpload()
            setIsExcelUploaded(false)
            return null
        }

        setIsLoadingProcess(true)
        if (!isSave) {
            dispatch(UploadMultiQuestionsExcel(fileInputKey, fileUploadName, "GetUploadMultiQuestionsExcel", "get", (e: any) => { uploadFileCallback(e) }))
        } else {
            dispatch(GetSaveUploadedQuestions(fileInputKey, "GetSaveUploadedQuestions", "get", () => { setIsCompleted(true); setIsLoadingProcess(false) }));
        }

    };

    const uploadFileCallback = (e: any) => {
        if (e == "error") {
            setIsLoadingProcess(false)
        }
    }

    const handleFileUploaded = (uppy: any) => {
        if (uppy) {
            let fileNames = getFileNames(uppy)
            setFileUploadName(fileNames[0]);
        }
    };

    const getFileNames = (uppy: any) => {
        const fileNames = Object.values(uppy.getState().files).map((file: any) => file.data.name);
        return fileNames;
    };

    const renderDistributions = (questionTypeName: string, validCount: number, invalidCount: number) => {
        let iconName = "";
        switch (questionTypeName) {
            case QuestionType.MultipleSingle.toString():
            case QuestionType.MultipleMultiple.toString():
                iconName = "format_list_bulleted";
                break;
            case QuestionType.Matching.toString():
                iconName = "shuffle";
                break;
            case QuestionType.Ordering.toString():
                iconName = "import_export";
                break;
            case QuestionType.OpenEnded.toString():
                iconName = "crop_landscape";
                break;
            default:
                iconName = "format_list_bulleted";
                break;
        }
        return (
            <tr>
                <td>
                    <i className="text-custom-dark material-icons ">{iconName}</i>
                </td>
                <td>
                    <label className="lead-label">{T.t(`r_question_type_${questionTypeName}`)}</label>
                </td>
                <td >

                    <span className='text-success'>{validCount > 0 ? validCount : "-"}</span> <span style={{ color: '#323231' }}>/</span> <span className='text-danger'>{invalidCount > 0 ? invalidCount : "-"}</span>
                </td>
            </tr>
        )
    }

    const handleOnclickWrongQuestionButton = () => {
        let filePath = wrongQuestionsFilePath;
        let linkUrl: string = window.appSettings.API_URL;

        if (filePath.startsWith('https:')) {
            linkUrl = filePath;
            _common.DownloadLink(linkUrl, '');
        } else if (filePath.startsWith('zip:')) {
            let zipurl = filePath.replace('zip:', '');
            linkUrl += '/' + zipurl
            _common.DownloadZip(linkUrl, T.t('r_assignment_files'))
        } else {
            if (filePath.startsWith('/')) {
                linkUrl += filePath;
            } else {
                linkUrl += '/' + filePath;
            }
            _common.DownloadLink(linkUrl, '');
        }
    }

    return (
        <Modal
            isOpen={isModalOpen}
            toggle={setCloseModal}
            contentLabel="Add Multiple Questions"
            modalClassName={'add-activity-modal modal-from-right slideInRight show animated add-question-modal'}
            fade={true}
            style={{ display: 'block', animationDuration: '0.5s' }}
            backdrop={'static'}
        >
            <div className="modal-dialog" style={{ width: '54vw' }}>
                <div className="modal-content">
                    <Formik
                        initialValues={{ questionText: '', questionType: 0 }}
                        onSubmit={onSubmit}
                    >
                        {formik => (
                            <form>
                                <div className="modal-header d-flex justify-content-start">
                                    <h5 className="modal-title d-flex align-items-center">
                                        <i className="material-icons mr-2">{_common.GetMaterialActivityIcons(ActivityType.Assignment)}</i>
                                        {T.t('r_add_multiple_questions')}
                                    </h5>
                                    <button
                                        onClick={() => setIsCompleted(true)}
                                        type="button"
                                        className="close"
                                        data-dismiss="modal"
                                        aria-label="Close"
                                    >
                                        <i style={{ color: '#000' }} className="material-icons">
                                            close
                                        </i>
                                        <span>{T.t('r_gen_close')}</span>
                                    </button>
                                </div>
                                <div className="modal-body p-2">
                                    <div className="import-source-content ">
                                        <div className="row mb-5">
                                            <div className="col-md-6">
                                                <div className="media">
                                                    <div className="bullet-points mr-3">1</div>
                                                    <div className="media-body pt-2">
                                                        <h5 className="mt-0">{T.t('r_questionBank_questionType_Choosing')}</h5>
                                                        {T.t('r_questionBank_questionType_Choosing_desc')}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <SelectNoneAsync
                                                    name={'questionType'}
                                                    touched={formik.touched.questionType}
                                                    errors={formik.errors.questionType}
                                                    setFieldValue={formik.setFieldValue}
                                                    value={formik.values.questionType}
                                                    setOptions={[
                                                        { value: QuestionType.MultipleSingle, label: T.t('r_gen_questionBank_questionType_multiple_single') },
                                                        // { value: QuestionType.MultipleMultiple, label: T.t('r_gen_questionBank_questionType_multiple_multiple') },
                                                        // { value: QuestionType.OpenEnded, label: T.t('r_gen_questionBank_questionType_open') },
                                                        // { value: QuestionType.Ordering, label: T.t('r_gen_questionBank_questionType_ordering') },
                                                        // { value: QuestionType.Matching, label: T.t('r_gen_questionBank_questionType_matching') }
                                                    ]}
                                                    disable={true}
                                                    onChange={(option: any) => formik.setFieldValue('questionType', option)}
                                                    //onChange={(e: any) => console.log(e, formik)}
                                                    isTouched={formik.setFieldTouched}
                                                    placeHolder={T.t('r_make_select')}
                                                    setSelectValue={[formik.values.questionType]}
                                                />
                                            </div>
                                        </div>
                                        <div className="row mb-5">
                                            <div className="col-md-6">
                                                <div className="media">
                                                    <div className="bullet-points mr-3">2</div>
                                                    <div className="media-body pt-2">
                                                        <h5 className="mt-0">{T.t('r_questionBank_excel_template_download')}</h5>
                                                        {T.t('r_questionBank_excel_template_download_desc')}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6 align-items-center">
                                                <ExcelTemplateDownload questionType={formik.values.questionType} />
                                            </div>
                                        </div>
                                        <div className="row pb-5 ">
                                            <div className="col-md-6">
                                                <div className="media">
                                                    <div className="bullet-points mr-3">3</div>
                                                    <div className="media-body pt-2">
                                                        <h5 className="mt-0">{T.t('r_questionBank_excel_template_upload')}</h5>
                                                        {T.t('r_questionBank_excel_template_upload_desc')}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <FileUpload
                                                    key={"static-file-upload"}
                                                    fileInputKey={fileInputKey}
                                                    height={250}
                                                    maxFileSize={10000000}
                                                    maxSelectedFileCount={1}
                                                    isCardImage={false}
                                                    //isModal={true}
                                                    //modalOpen={this.state.showFileUploader}
                                                    //handleModalClose={this.closeFileUploader}
                                                    triggerFileUpload={handleFileUploaded}
                                                    fileTypes={".xlsx"}
                                                />
                                            </div>
                                        </div>
                                        <div className="import-source-counter p-2">
                                            <div className="row">
                                                <div className="col-md-6 ">
                                                    <label className="lead-label mb-2 text-uppercase">{T.t('r_total_question_count')}</label>
                                                    <span className="d-block total-source-number mb-4">{allquestionCount}</span>
                                                    <div className="d-flex justify-content-start mb-3">
                                                        <div className="pr-3">
                                                            <label className="lead-label text-uppercase">{T.t('r_total_valid_question_count')}</label>
                                                            <span className="d-block import-source-number text-success">{validquestionCount}</span>
                                                        </div>
                                                        <div className="pr-3">
                                                            <label className="lead-label text-uppercase">{T.t('r_total_invalid_question_count')}</label>
                                                            <span className="d-block import-source-number text-danger">{invalidquestionCount}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="lead-label mb-2 text-uppercase">{T.t('r_distribution_of_question_type')}</label>
                                                    <table className="w-100">
                                                        <tbody className="import-source-table ">
                                                            {
                                                                questionTypeDistributionArray.map((item: any) => renderDistributions(item.returnTypeName, item.validCount, item.invalidCount))

                                                            }
                                                        </tbody>


                                                    </table>
                                                    {
                                                        invalidquestionCount > 0 && wrongQuestionsFilePath.length > 0 &&
                                                        <div className='d-flex justify-content-end mt-2'>
                                                            <button type="button" className="btn btn-danger" onClick={handleOnclickWrongQuestionButton}>
                                                                <i className="material-icons mt-1 mr-2">download</i> {T.t('r_download_wrong_questions')}
                                                            </button>
                                                        </div>
                                                    }

                                                </div>

                                            </div>

                                        </div>
                                    </div>
                                </div>

                                <div className="modal-footer d-block">
                                    <div className="row">
                                        <div className="col-md">
                                            <button type="button" className="btn btn-gray" onClick={() => setIsCompleted(true)}>{T.t('r_cancel')}</button>
                                        </div>
                                        <div className="col-md text-md-right">
                                            <button type="button" disabled={isLoadingProcess} className="btn btn-green" onClick={() => saveQuestions(isExcelUploaded)}>
                                                <i className="material-icons mr-2 icon-rtl">done</i> {isLoadingProcess ? T.t('r_gen_loading') : !isExcelUploaded ? T.t('r_upload_questions') : T.t('r_save_uploaded_questions')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </form>
                        )}
                    </Formik>
                </div>
            </div>
        </Modal>
    );
};

export default AddMultipleQuestion;
